/* eslint-disable @typescript-eslint/member-ordering, @ngrx/prefer-effect-callback-in-block-statement*/
import { inject, Injectable } from '@angular/core';

import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  tap,
} from 'rxjs/operators';

import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AzureAuthService } from '../../azure-auth.service';
import { AccountInfo, LoadProfileImageError } from '../../models';
import {
  loadProfileImage,
  loadProfileImageFailure,
  loadProfileImageSuccess,
  loadSapUserId,
  loadSapUserIdFailure,
  loadSapUserIdSuccess,
  login,
  loginSuccess,
  logout,
} from '../actions/auth.actions';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AzureAuthService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  public login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(login),
        tap(() => this.authService.login())
      ),
    { dispatch: false }
  );

  public logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => this.authService.logout())
      ),
    { dispatch: false }
  );

  public profileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProfileImage),
      mergeMap(() =>
        this.authService.getProfileImage().pipe(
          map((url) =>
            loadProfileImageSuccess({
              url,
            })
          ),
          catchError((errorMessage: LoadProfileImageError) =>
            of(
              loadProfileImageFailure({
                errorMessage: errorMessage.message,
              })
            )
          )
        )
      )
    )
  );

  public sapUserId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSapUserId),
      mergeMap(() =>
        this.authService.getSapUserId().pipe(
          map((sapUserId) => loadSapUserIdSuccess({ sapUserId })),
          catchError(() => of(loadSapUserIdFailure()))
        )
      )
    )
  );

  public inProgress$ = createEffect(() =>
    this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      mergeMap(() => this.authService.handleAccount()),
      filter((accountInfo: AccountInfo) => accountInfo !== undefined),
      distinctUntilChanged(
        (old: AccountInfo, current: AccountInfo) =>
          (old.idTokenClaims as any)?.nonce ===
          (current.idTokenClaims as any)?.nonce
      ),
      mergeMap((accountInfo: AccountInfo) => [
        loginSuccess({ accountInfo }),
        loadProfileImage(),
        loadSapUserId(),
      ])
    )
  );
}
